// src/components/LaunchCelebration.tsx

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Dumbbell, ArrowRight } from "lucide-react";


import dagdaLogo from "../../assets/logo.png";
import gymOneLogo from "../../assets/gforce.jpg";
import gymTwoLogo from "../../assets/SBG.png";
import "../../styles/launchcelebration.css";



interface LaunchCelebrationProps {
  onClose: () => void;
}

const LaunchCelebration = ({ onClose }: LaunchCelebrationProps) => {
  const [visible, setVisible] = useState(true);

  const closeLaunch = () => {
    setVisible(false);

    setTimeout(() => {
      onClose();
    }, 450);
  };

  useEffect(() => {
    // Prevent homepage scrolling while launch screen is open
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="launch-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 1.03,
          }}
          transition={{ duration: 0.45 }}
        >
          {/* Decorative background */}
          <div className="launch-grid" />
          <div className="launch-glow launch-glow-one" />
          <div className="launch-glow launch-glow-two" />

          {/* Celebration particles */}
          <div className="celebration-particles">
            {[...Array(24)].map((_, index) => (
              <motion.span
                key={index}
                className={`particle particle-${(index % 5) + 1}`}
                initial={{
                  y: -100,
                  opacity: 0,
                  rotate: 0,
                }}
                animate={{
                  y: "110vh",
                  opacity: [0, 1, 1, 0],
                  rotate: 360,
                }}
                transition={{
                  duration: 4 + Math.random() * 3,
                  delay: Math.random() * 2,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  left: `${Math.random() * 100}%`,
                }}
              />
            ))}
          </div>

          {/* Close */}
          <motion.button
            className="launch-close"
            onClick={closeLaunch}
            aria-label="Close launch announcement"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 }}
            whileHover={{
              scale: 1.08,
              rotate: 5,
            }}
            whileTap={{
              scale: 0.9,
            }}
          >
            <X size={25} />
          </motion.button>

          <div className="launch-container">
            {/* Top Dagda logo */}
            <motion.div
              className="launch-dagda-logo"
              initial={{
                opacity: 0,
                y: -25,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.7,
              }}
            >
              <img src={dagdaLogo} alt="Dagda Nutrition" />
            </motion.div>

            {/* Badge */}
            <motion.div
              className="launch-badge"
              initial={{
                opacity: 0,
                scale: 0.8,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                delay: 0.2,
                duration: 0.6,
              }}
            >
              <Sparkles size={16} />

              <span>BIG NEWS</span>

              <Sparkles size={16} />
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{
                opacity: 0,
                y: 30,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.3,
                duration: 0.8,
              }}
            >
              WE'RE OFFICIALLY
              <span> LAUNCHING.</span>
            </motion.h1>

            <motion.p
              className="launch-description"
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.45,
                duration: 0.8,
              }}
            >
              Dagda Nutrition is bringing smarter, faster and more convenient
              nutrition directly into your gym.
            </motion.p>

            {/* Partner heading */}
            <motion.div
              className="launch-partner-label"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              transition={{
                delay: 0.65,
              }}
            >
              <span />

              <p>LAUNCHING WITH</p>

              <span />
            </motion.div>

            {/* Gym logos */}
            {/* Gym logos */}
<motion.div
  className="gym-partners"
  initial={{
    opacity: 0,
    y: 30,
  }}
  animate={{
    opacity: 1,
    y: 0,
  }}
  transition={{
    delay: 0.75,
    duration: 0.8,
  }}
>
  <motion.a
    href="https://gforcefitness.ie/"
    target="_blank"
    rel="noopener noreferrer"
    className="gym-logo-card"
    aria-label="Visit GForce website"
    whileHover={{
      y: -7,
      scale: 1.02,
    }}
    whileTap={{
      scale: 0.97,
    }}
  >
    <img src={gymOneLogo} alt="GForce" />
  </motion.a>

  <div className="partner-plus">+</div>

  <motion.a
    href="https://sbgireland.com"
    target="_blank"
    rel="noopener noreferrer"
    className="gym-logo-card"
    aria-label="Visit SBG Ireland website"
    whileHover={{
      y: -7,
      scale: 1.02,
    }}
    whileTap={{
      scale: 0.97,
    }}
  >
    <img src={gymTwoLogo} alt="SBG Ireland" />
  </motion.a>
</motion.div>

            {/* Bottom message */}
            <motion.div
              className="launch-bottom"
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 1,
                duration: 0.8,
              }}
            >
              <Dumbbell size={18} />

              <p>
                Fuel your workout.
                <strong> Your way.</strong>
              </p>
            </motion.div>

            <motion.button
              className="launch-enter-button"
              onClick={closeLaunch}
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 1.15,
              }}
              whileHover={{
                scale: 1.03,
              }}
              whileTap={{
                scale: 0.97,
              }}
            >
              Explore Dagda

              <ArrowRight size={18} />
            </motion.button>
          </div>

          <motion.div
            className="launch-scroll-message"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 0.6,
            }}
            transition={{
              delay: 1.5,
            }}
          >
            DAGDA NUTRITION · SMART NUTRITION · POWERED BY OrangeCape
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LaunchCelebration;